// Admin → Road Data Sources (v1.2, 2026-09-05): every upstream the map/road-intel plug-ins can use, with an honest
// "what it learns about us" line, the cost, the request budget and this month's usage from the privacy shield ledger.
// Government open data is polled by our server; commercial vendors are OFF by default and need an explicit
// acknowledgement + an API key (entered here once, stored on the traffic service only, never shown again).
// "Let phones open live streams directly" is the one per-source choice that exposes a device to an upstream.
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Grid, IconButton, Snackbar, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Traffic as TrafficIcon, Refresh as RefreshIcon, Public as GovIcon, Business as VendorIcon, VpnKey as KeyIcon, Shield as ShieldIcon } from '@mui/icons-material';
import api from '../services/api';

type Usage = { requests_month: number; requests_hour: number; last_request: number | null; errors_month: number };
type Provider = { name: string; label: string; enabled: boolean; third_party: boolean; layers: string[]; upstream: string; collects: string; cost: string;
  default: boolean; last_ok: number | null; last_error: string | null; device_direct: boolean | null; device_direct_option: boolean;
  key_required: boolean; key_set: boolean | null; shield: boolean; budget_month: number | null; budget_hour: number | null; usage: Usage };

export default function RoadSourcesSettings() {
  const [items, setItems] = useState<Provider[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ack, setAck] = useState<Provider | null>(null);
  const [keyFor, setKeyFor] = useState<Provider | null>(null);
  const [keyVal, setKeyVal] = useState('');
  const [budgetFor, setBudgetFor] = useState<Provider | null>(null);
  const [bm, setBm] = useState(''); const [bh, setBh] = useState('');
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = async () => {
    try { const r = await api.get('/road-intel/providers'); setItems(r.data.providers || []); }
    catch (e: any) { setSnack({ msg: e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Could not load road data sources', sev: 'error' }); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async (p: Provider, patch: any, okMsg?: string) => {
    setBusy(p.name);
    try { const r = await api.put(`/road-intel/providers/${p.name}`, patch); setItems(r.data.providers || []); setSnack({ msg: okMsg || `${p.label}: saved`, sev: 'success' }); }
    catch (e: any) {
      const d = e?.response?.data?.detail;
      if (d && d.error === 'third_party_ack_required') { setAck(p); return; }
      setSnack({ msg: (d && (d.message || d.error)) || 'Save failed', sev: 'error' });
    } finally { setBusy(null); }
  };
  const toggle = (p: Provider) => {
    if (!p.enabled && p.third_party) { setAck(p); return; }
    save(p, { enabled: !p.enabled });
  };
  const ago = (t: number | null) => !t ? 'never' : `${Math.max(0, Math.round((Date.now() / 1000 - t) / 60))} min ago`;
  const errTxt = (e: string | null) => { if (!e) return null; const i = e.indexOf('|'); return i > 0 ? `${e.slice(i + 1)} (${ago(Number(e.slice(0, i)))})` : e; };
  const pct = (p: Provider) => p.budget_month ? Math.min(100, Math.round(100 * p.usage.requests_month / p.budget_month)) : null;

  if (!items) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  const gov = items.filter((p) => !p.third_party), vendors = items.filter((p) => p.third_party);
  const card = (p: Provider) => (
    <Grid item xs={12} md={6} key={p.name}>
      <Card variant="outlined" sx={{ opacity: p.enabled ? 1 : 0.8, height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {p.third_party ? <VendorIcon sx={{ color: '#E30613' }} /> : <GovIcon sx={{ color: '#1E4A6B' }} />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#1E4A6B' }}>{p.label}</Typography>
              <Typography variant="caption" color="text.secondary">{p.upstream}</Typography>
            </Box>
            <Switch checked={p.enabled} disabled={busy === p.name} onChange={() => toggle(p)} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            <Chip size="small" label={p.third_party ? 'Commercial third party' : 'Government / open data'} sx={{ bgcolor: p.third_party ? '#fdecec' : '#e8f1f8', color: p.third_party ? '#B00010' : '#1E4A6B', fontWeight: 600 }} />
            {p.layers.map((l) => <Chip key={l} size="small" label={l.replace('_', ' ')} />)}
            {p.shield && <Tooltip title="Fetched through the privacy shield: rotating browser identity, no cookies, jittered timing, coarse grid cells + decoy cells, request ledger"><Chip size="small" icon={<ShieldIcon />} label="shielded" variant="outlined" sx={{ color: '#1E4A6B' }} /></Tooltip>}
            {p.key_required && <Chip size="small" icon={<KeyIcon />} label={p.key_set ? 'key entered' : 'key needed'} color={p.key_set ? 'success' : 'warning'} variant="outlined" />}
            <Chip size="small" label={p.enabled ? `last poll ${ago(p.last_ok)}` : 'off'} variant="outlined" />
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: '#4B5867' }}><b>What it learns about us:</b> {p.collects}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#4B5867' }}><b>Cost:</b> {p.cost}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#4B5867' }}>
            <b>Requests:</b> {p.usage.requests_month.toLocaleString()} this month{p.budget_month ? ` of ${p.budget_month.toLocaleString()} budget (${pct(p)}%)` : ''}, {p.usage.requests_hour} this hour{p.budget_hour ? ` of ${p.budget_hour}` : ''}
            {p.usage.errors_month ? `, ${p.usage.errors_month} errors` : ''}
          </Typography>
          {p.last_error && <Typography variant="caption" sx={{ color: '#B00010' }}>Last problem: {errTxt(p.last_error)}</Typography>}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.key_required && <Button size="small" variant="outlined" startIcon={<KeyIcon />} onClick={() => { setKeyVal(''); setKeyFor(p); }}>{p.key_set ? 'Replace key' : 'Enter API key'}</Button>}
            {(p.budget_month || p.budget_hour) && <Button size="small" onClick={() => { setBm(String(p.budget_month ?? '')); setBh(String(p.budget_hour ?? '')); setBudgetFor(p); }}>Change budget</Button>}
            {p.device_direct_option && (
              <FormControlLabel control={<Switch size="small" checked={!!p.device_direct} disabled={!p.enabled || busy === p.name} onChange={() => save(p, { device_direct: !p.device_direct })} />}
                label={<Typography variant="body2">Let phones open live streams directly from {p.upstream.split(' ')[0]}</Typography>} />
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrafficIcon sx={{ color: '#1E4A6B' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>Road data sources</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Reload</Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={<ShieldIcon />}>
        These feed the map layers in Dispatch and the staff app. Phones never talk to any upstream — our server does, through the <b>privacy shield</b>:
        it asks for coarse 28 km grid squares (where the fleet has been, plus random decoy squares), in shuffled order with random timing, with a different
        mainstream browser identity on every request and no cookies, and it counts every request against a budget so a free allowance is never exceeded.
        A keyed vendor still sees its key on each request — that cannot be hidden, so it is stated on the card. Switch any source off the moment it misbehaves.
      </Alert>
      <Typography variant="subtitle2" sx={{ color: '#1E4A6B', mb: 1 }}>Government / open data — free</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>{gov.map(card)}</Grid>
      <Typography variant="subtitle2" sx={{ color: '#B00010', mb: 1 }}>Commercial vendors — off by default, need your acknowledgement and a key</Typography>
      <Grid container spacing={2}>{vendors.map(card)}</Grid>

      <Dialog open={!!ack} onClose={() => setAck(null)}>
        <DialogTitle>Turn on a commercial third party?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}><b>{ack?.label}</b> is a commercial vendor.</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><b>What it will learn:</b> {ack?.collects}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><b>Cost:</b> {ack?.cost}</Typography>
          <Typography variant="body2">Phones still only talk to backoffice.concept.bz. Requests stop automatically at the budget. You can switch it off again at any time.
            {ack?.key_required && !ack?.key_set ? ' You will also need to enter the API key.' : ''}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAck(null)}>Keep it off</Button>
          <Button variant="contained" color="error" onClick={() => { const p = ack!; setAck(null); save(p, { enabled: true, acknowledge_third_party: true }); }}>I understand — turn it on</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!keyFor} onClose={() => setKeyFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>API key — {keyFor?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Stored on the traffic service only; never sent to a phone and never shown again. {keyFor?.cost}</Typography>
          <TextField fullWidth type="password" label="API key / token" value={keyVal} onChange={(e) => setKeyVal(e.target.value)} autoComplete="off" />
        </DialogContent>
        <DialogActions>
          {keyFor?.key_set && <Button color="error" onClick={() => { const p = keyFor!; setKeyFor(null); save(p, { api_key: '' }, `${p.label}: key removed`); }}>Remove key</Button>}
          <Button onClick={() => setKeyFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!keyVal.trim()} onClick={() => { const p = keyFor!; setKeyFor(null); save(p, { api_key: keyVal.trim() }, `${p.label}: key saved`); }}>Save key</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!budgetFor} onClose={() => setBudgetFor(null)}>
        <DialogTitle>Request budget — {budgetFor?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>The shield stops asking when a ceiling is reached, so a free allowance is never exceeded. {budgetFor?.cost}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Per month" type="number" value={bm} onChange={(e) => setBm(e.target.value)} />
            <TextField label="Per hour" type="number" value={bh} onChange={(e) => setBh(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => { const p = budgetFor!; setBudgetFor(null); const patch: any = {}; if (bm) patch.budget_month = Number(bm); if (bh) patch.budget_hour = Number(bh); save(p, patch); }}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}><Alert severity={snack?.sev || 'success'} onClose={() => setSnack(null)}>{snack?.msg}</Alert></Snackbar>
    </Box>
  );
}
